import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Col, Form, Row, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getWebhookLogsAPI, replayWebhookAPI } from '../../../Services/mockApi';
import { useInterval } from '../../../Hooks/useInterval';
import PageHeader from '../../../Components/shared/PageHeader';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './WebhookMonitor.module.scss';

const PROVIDERS = [
  { value: '', label: 'All providers' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'google', label: 'Google' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const STATUS_GROUPS = [
  { value: '', label: 'All statuses' },
  { value: '200', label: '200' },
  { value: '4xx', label: '4xx' },
  { value: '5xx', label: '5xx' },
];

const RANGES = [
  { value: '1h', label: 'Last 1h' },
  { value: '6h', label: 'Last 6h' },
  { value: '24h', label: 'Last 24h' },
];

function statusBadge(status) {
  if (status >= 500) return { bg: 'danger', label: status };
  if (status >= 400) return { bg: 'warning', text: 'dark', label: status };
  return { bg: 'success', label: status };
}

function canReplayWebhook() {
  return localStorage.getItem('mh_webhook_replay') === '1';
}

export default function WebhookMonitor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState('');
  const [statusGroup, setStatusGroup] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [timeRange, setTimeRange] = useState('24h');
  const [drawer, setDrawer] = useState(null);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [replayId, setReplayId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getWebhookLogsAPI({
        provider: provider || undefined,
        statusGroup: statusGroup || undefined,
        tenantId: tenantId.trim() || undefined,
        timeRange,
      });
      setRows(res.data || []);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [provider, statusGroup, tenantId, timeRange]);

  useEffect(() => {
    load();
  }, [load]);

  useInterval(() => {
    load();
  }, 30000);

  const columns = useMemo(
    () => [
      {
        id: 'receivedAt',
        name: 'Time',
        selector: (r) => r.receivedAt,
        sortable: true,
        sortFunction: (a, b) => new Date(a.receivedAt) - new Date(b.receivedAt),
        format: (r) => new Date(r.receivedAt).toLocaleString(),
      },
      {
        name: 'Provider',
        selector: (r) => r.provider,
        sortable: true,
      },
      {
        name: 'Tenant',
        selector: (r) => r.tenantCode,
        sortable: true,
      },
      {
        name: 'Endpoint',
        selector: (r) => r.endpoint,
        grow: 2,
        cell: (r) => (
          <span title={r.endpoint}>
            {r.endpoint?.length > 36 ? `${r.endpoint.slice(0, 36)}…` : r.endpoint}
          </span>
        ),
      },
      {
        id: 'httpStatus',
        name: 'Status',
        selector: (r) => r.httpStatus,
        sortable: true,
        cell: (r) => {
          const st = statusBadge(r.httpStatus);
          return (
            <Badge bg={st.bg} text={st.text}>
              {st.label}
            </Badge>
          );
        },
      },
      {
        id: 'durationMs',
        name: 'Latency (ms)',
        selector: (r) => r.durationMs,
        sortable: true,
      },
      {
        name: 'Replay',
        ignoreRowClick: true,
        cell: (r) => (
          <Button size="sm" variant="outline-secondary" onClick={() => setReplayId(r.id)}>
            Replay
          </Button>
        ),
      },
    ],
    []
  );

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader
          title="Webhook monitor"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Integrations', path: platformUrl('integrations') },
            { label: 'Webhooks' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load webhooks"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Webhook monitor"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Integrations', path: platformUrl('integrations') },
          { label: 'Webhooks' },
        ]}
      />

      <Row className="g-2 mb-3 align-items-end">
        <Col md={6} lg={3}>
          <Form.Label className="small text-muted">Provider</Form.Label>
          <Form.Select value={provider} onChange={(e) => setProvider(e.target.value)}>
            {PROVIDERS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6} lg={2}>
          <Form.Label className="small text-muted">Status</Form.Label>
          <Form.Select value={statusGroup} onChange={(e) => setStatusGroup(e.target.value)}>
            {STATUS_GROUPS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6} lg={3}>
          <Form.Label className="small text-muted">Tenant ID</Form.Label>
          <Form.Control
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="tnt-1001"
          />
        </Col>
        <Col md={6} lg={2}>
          <Form.Label className="small text-muted">Range</Form.Label>
          <Form.Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            {RANGES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={12} lg={2} className="text-lg-end">
          <div className="small text-muted">
            Last refresh: {lastRefresh ? lastRefresh.toLocaleTimeString() : '—'}
          </div>
          <Button size="sm" variant="outline-primary" className="mt-1" onClick={() => load()} disabled={loading}>
            Refresh now
          </Button>
        </Col>
      </Row>

      <DataTableWrapper
        columns={columns}
        data={rows}
        loading={loading && !rows.length}
        onRowClick={(r) => {
          setDrawer(r);
          setBodyExpanded(false);
        }}
        showSearch={false}
        keyField="id"
        customTableProps={{
          fixedHeader: true,
          fixedHeaderScrollHeight: '520px',
          defaultSortFieldId: 'receivedAt',
          defaultSortAsc: false,
        }}
      />

      <DrawerPanel
        isOpen={Boolean(drawer)}
        onClose={() => setDrawer(null)}
        title="Webhook delivery"
        width={520}
      >
        {drawer && (
          <>
            <h6 className="small text-muted">Headers</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                {Object.entries(drawer.headers || {}).map(([k, v]) => (
                  <tr key={k}>
                    <td className="text-break">{k}</td>
                    <td className="text-break">{v}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <h6 className="small text-muted">Body</h6>
            <pre className={styles.payload}>
              {bodyExpanded ? drawer.body : `${(drawer.body || '').slice(0, 280)}${(drawer.body || '').length > 280 ? '…' : ''}`}
            </pre>
            <Button size="sm" variant="outline-secondary" className="mb-2" onClick={() => setBodyExpanded((v) => !v)}>
              {bodyExpanded ? 'Collapse' : 'Expand'}
            </Button>
            <div className="mb-2">
              <Badge bg={drawer.signatureValid ? 'success' : 'danger'}>
                Signature: {drawer.signatureValid ? 'HMAC valid' : 'HMAC invalid'}
              </Badge>
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => {
                toast.success('Download started (simulated)');
              }}
            >
              Download payload
            </Button>
          </>
        )}
      </DrawerPanel>

      <ConfirmModal
        isOpen={Boolean(replayId)}
        onClose={() => setReplayId(null)}
        onConfirm={async () => {
          try {
            await replayWebhookAPI(replayId);
            toast.success('Replay queued');
            setReplayId(null);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Replay webhook?"
        message={
          canReplayWebhook() ? (
            'This may duplicate side effects for the tenant.'
          ) : (
            <span>
              <strong>Elevated role required.</strong> Set localStorage key <code>mh_webhook_replay</code> to{' '}
              <code>1</code> in this environment to acknowledge. Proceeding still queues a simulated replay.
            </span>
          )
        }
        confirmLabel="Replay"
        danger
      />
    </div>
  );
}
